import { dayLabel, fromIsoDay, hourLabel, isoDay, monthTitle, shortDayLabel } from 'helper/timeLabels';

/**
 * The activity of a record in one shape, whatever it comes from: the calls,
 * e-mails, appointments, tasks and notes of the sales team, and what the
 * client did in Jobbiz. Pure: the texts come from `text` (recordActivity.*).
 */

export const ACTIVITY_FILTERS = ['all', 'email', 'call', 'task', 'note', 'client'];

// What the client does in Jobbiz, as the api traces it. `minor`: too frequent
// for « Tout », kept for « Actions clients »; `tab`: the tab of the company that shows it.
export const CLIENT_EVENTS = {
  mission_created: { tab: 'missions' },
  mission_canceled: { tab: 'missions' },
  contract_signed: { tab: 'missions', minor: true },
  timesheet_validated: { tab: 'missions', minor: true },
  worker_review: { tab: 'missions' },
  member_added: { tab: 'contacts' },
};

/**
 * Every line of a record's activity. `record`: { calls, emails, events,
 * tasks, notes, clientEvents }; `nameOf(id)`: a colleague's name; `contact`:
 * on a contact's page, the name that need not be repeated.
 */
// A note of the api says who may edit it or delete it (`canEdit`,
// `canDelete`); the activity offers those gestures only.
const noteLine = (note, { text, nameOf }) => ({
  key: `n${note.id}`,
  kind: 'note',
  at: new Date(note.at ?? note.createdAt),
  title: text('noteBy', { name: nameOf(note.authorId) }),
  body: note.body,
  note:
    note.canEdit || note.canDelete
      ? { id: note.id, body: note.body, links: note.links ?? [], canEdit: Boolean(note.canEdit), canDelete: Boolean(note.canDelete) }
      : null,
});

export const buildActivity = (record, { now, text, nameOf, contact = null }) => {
  const out = [];
  // A note written on a call shows under it, and not again among the notes.
  const callIds = new Set((record.calls ?? []).map((call) => call.id));
  const callNotes = (record.notes ?? []).filter((note) => note.callId && callIds.has(note.callId));
  (record.calls ?? []).forEach((call) => {
    const at = new Date(call.at);
    const who = call.contactName && call.contactName !== contact ? ` · ${call.contactName}` : '';
    out.push({
      key: `c${call.id}`,
      kind: 'call',
      at,
      title: `${text('callBy', { name: nameOf(call.authorId) })}${who}`,
      body: call.note || text(`callResult.${call.result}`),
      // A call of the api takes a note: `noteLink`, the record it goes to.
      ref: call.noteLink ? { kind: 'call', id: call.id, link: call.noteLink } : undefined,
      notes: callNotes
        .filter((note) => note.callId === call.id)
        .map((note) => noteLine(note, { text, nameOf }))
        .sort((a, b) => a.at - b.at),
    });
  });
  (record.emails ?? []).forEach((email) => {
    const sent = email.direction === 'sent';
    const at = new Date(email.at);
    // A programmed e-mail waits in « À venir » until its time, then reads as sent.
    const future = sent && Boolean(email.scheduledAt) && at > now;
    const address = sent && email.to?.length ? email.to.join(', ') : email.address;
    out.push({
      key: `m${email.id}`,
      kind: 'email',
      at,
      future,
      when: future ? `${dayLabel(at)} · ${hourLabel(at)}` : undefined,
      title: text(future ? 'emailScheduled' : sent ? 'emailTo' : 'emailFrom', { address }),
      subject: email.subject,
      body: email.body,
      header: sent
        ? text('emailHeaderSent', { from: nameOf(email.authorId), to: email.contactName, address: email.address })
        : text('emailHeaderReceived', { from: email.contactName || email.address, to: nameOf(email.authorId) }),
      opens: sent && !future ? (email.opens ?? []).map((day) => new Date(day)) : null,
      cancel: future ? { id: email.id, ...(email.groupId ? { groupId: email.groupId } : { companyId: email.companyId }) } : null,
    });
  });
  (record.events ?? []).forEach((event) => {
    const start = new Date(event.startsAt);
    const future = start > now;
    const mode = event.mode ? ` · ${event.mode}` : '';
    const withWhom = event.contactName && event.contactName !== contact
      ? text('meetingWith', { name: event.contactName })
      : text('meeting');
    out.push({
      key: `e${event.id}`,
      kind: 'event',
      at: start,
      future,
      title: future ? event.title || withWhom : withWhom,
      when: `${future ? dayLabel(start) : shortDayLabel(isoDay(start))} · ${hourLabel(start)}${mode}`,
      body: event.outcome || event.description,
    });
  });
  (record.tasks ?? []).forEach((task) => {
    const done = Boolean(task.doneAt);
    out.push({
      key: `t${task.id}`,
      kind: 'task',
      at: done ? new Date(task.doneAt) : fromIsoDay(task.dueOn),
      future: !done,
      title: text(done ? 'taskDoneBy' : 'taskFor', { name: nameOf(task.assigneeId) }),
      when: done
        ? shortDayLabel(isoDay(new Date(task.doneAt)))
        : text('taskDue', { day: dayLabel(fromIsoDay(task.dueOn)).toLowerCase() }),
      body: task.title,
      ref: { kind: 'task', id: task.id },
    });
  });
  (record.notes ?? []).forEach((note) => {
    if (!callNotes.includes(note)) out.push(noteLine(note, { text, nameOf }));
  });
  (record.clientEvents ?? []).forEach((event) => {
    const def = CLIENT_EVENTS[event.kind];
    if (!def) return;
    // A review says whether it is good or bad in its own words.
    const key =
      event.kind === 'worker_review'
        ? `client.worker_review_${event.details?.feedback === 'negative' ? 'negative' : 'positive'}`
        : `client.${event.kind}`;
    const action = text(key, event.details ?? {});
    out.push({
      key: `x${event.id}`,
      kind: 'client',
      eventKind: event.kind,
      details: event.details ?? {},
      at: new Date(event.at),
      minor: Boolean(def.minor),
      self: Boolean(contact) && event.actor === contact,
      actor: event.actor,
      action,
      ref: { kind: 'client', tab: def.tab, companyId: event.companyId },
    });
  });
  return out;
};

// The activity of several records at once (a group and its companies): each
// line says which one it comes from.
export const sourcesActivity = (sources, options) =>
  sources.flatMap((source) =>
    buildActivity(source.record, options).map((item) => ({
      ...item,
      key: `${source.id}-${item.key}`,
      source: source.id,
      from: source.label,
      ref: item.ref && { ...item.ref, companyId: item.ref.companyId ?? source.companyId },
    })),
  );

// An appointment is a task: « Tâches » shows both. « Tout » leaves out the
// client's minor actions.
export const inFilter = (item, filter) => {
  if (filter === 'all') return item.kind !== 'client' || !item.minor;
  return item.kind === filter || (filter === 'task' && item.kind === 'event');
};

// The client's actions of a kind, on a day and in a record, in one line that
// unfolds; alone, an action keeps its line.
export const bundleClient = (items) => {
  const out = [];
  const open = new Map();
  items.forEach((item) => {
    if (item.kind !== 'client') {
      out.push(item);
      return;
    }
    const key = `${item.source ?? ''}|${item.eventKind}|${isoDay(item.at)}`;
    if (open.has(key)) open.get(key).bundle.push(item);
    else {
      const bundle = { ...item, key: `b-${item.key}`, bundle: [item] };
      open.set(key, bundle);
      out.push(bundle);
    }
  });
  return out.map((item) => (item.bundle?.length === 1 ? item.bundle[0] : item));
};

/**
 * What a filter shows: what is to come (the soonest first), then the past by
 * month (the latest first), the client's actions of a day bundled.
 */
export const activitySections = (items, filter) => {
  const shown = items.filter((item) => inFilter(item, filter));
  const upcoming = shown.filter((item) => item.future).sort((a, b) => a.at - b.at);
  const past = bundleClient(shown.filter((item) => !item.future).sort((a, b) => b.at - a.at));
  const months = [];
  past.forEach((item) => {
    const label = monthTitle(item.at);
    if (!months.length || months[months.length - 1].label !== label) months.push({ label, items: [] });
    months[months.length - 1].items.push(item);
  });
  return { count: shown.length, upcoming, months };
};

export const countByFilter = (items) =>
  Object.fromEntries(ACTIVITY_FILTERS.map((filter) => [filter, items.filter((item) => inFilter(item, filter)).length]));
